/* =========================
   PART 1 — DRAWER (v38 mk3)
   - One-open-per-level for TOP/L2/L3/L4 (tops covers Simple Mega vs Tabbed Mega)
   - L4 (category) recall kept
   - derive() prefers data-model / data-make
   ========================= */
(() => {
  const KEY='hrb_menu_state_v9',
        VER='2025-08-18-mk3',
        TTL=30*24*60*60*1000;
  const DEBUG=/\bmenu_debug=1\b/.test(location.search);
  const log=(...a)=>{ if (DEBUG) console.log('[HRB v38]',...a); };
  const now=()=>Date.now?Date.now():new Date().getTime();

  const read=()=>{ try{
    const raw=localStorage.getItem(KEY); if(!raw) return null;
    const d=JSON.parse(raw);
    if(!d||d.version!==VER||!d.ts||(now()-d.ts)>TTL){ localStorage.removeItem(KEY); return null; }
    return d;
  }catch{ return null; } };
  const write=o=>{ try{ localStorage.setItem(KEY, JSON.stringify({...o,version:VER,ts:now()})); }catch{} };

  const activeDrawer = () => document.querySelector('details.menu-drawer-container[open]') || null;
  const activeUL     = () => activeDrawer()?.querySelector('.menu-drawer__menu') || null;

  const tops = U => Array.from(U?.querySelectorAll(':scope > li > details')||[]);
  const kids = D => Array.from(D?.querySelectorAll('details')||[])
                          .filter(x => x.parentElement?.closest('details')===D);

  // One-open-per-level: TOP, L2, L3, L4
  function closeSiblingsAtSameLevel(d){
    if(!d) return;
    const lvl=(d.getAttribute('data-level')||'').toLowerCase();
    if(!/^(top|l2|l3|l4)$/.test(lvl)) return;

    let list=[];
    if(lvl==='top'){
      const U=activeUL(); if(!U) return;
      list = tops(U);
    }else{
      const parent=d.parentElement?.closest('details');
      if(!parent) return;
      list = kids(parent);
    }
    list.forEach(sib=>{ if(sib!==d && sib.open) sib.open=false; });
  }

  const norm=s=>(s||'').toString().trim().toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9\-_]/g,'');

  // prefer data-model, then data-make, then handle/URL/text
  const derive = d => {
    if(!d) return null;
    const m  = d.getAttribute('data-model'); if(m)  return norm(m);
    const mk = d.getAttribute('data-make');  if(mk) return norm(mk);
    const h  = d.getAttribute('data-handle'); if(h) return norm(h);
    const a  = d.querySelector('summary a[href]');
    if(a){ try{
      const u=new URL(a.getAttribute('href'), location.origin);
      const parts=u.pathname.split('/').filter(Boolean);
      if(parts.length) return norm(parts.at(-1));
    }catch{} }
    const t=(d.querySelector('summary')?.textContent||'').trim();
    return t?norm(t):null;
  };

  const last = { top:null, l2:null, l3:null, l4:null };
  const occIndex=(list,node,h)=>{ let oc=0; for(const d of list){ const dh=derive(d); if(d===node) return oc; if(dh===h) oc++; } return 0; };
  const pickByHandleOcc=(list,h,oc)=>{ const m=list.filter(d=>derive(d)===h); if(!m.length) return null; return (typeof oc==='number'&&oc>=0&&oc<m.length)?m[oc]:m[0]; };

  function chooseTopOpen(U, hint){
    if(hint?.top && hint.top.open && tops(U).includes(hint.top)) return hint.top;
    const all = Array.from(U.querySelectorAll(':scope > li > details[open]'));
    return all.includes(last.top) ? last.top : (all[all.length-1] || null);
  }
  function chooseL2Open(top, hint){
    if(!top) return null;
    const L2=kids(top);
    if(hint?.l2 && hint.l2.open && L2.includes(hint.l2)) return hint.l2;
    const open=L2.filter(d=>d.open);
    return open.includes(last.l2) ? last.l2 : (open[open.length-1] || null);
  }
  function chooseL3Open(l2, hint){
    if(!l2) return null;
    const L3=kids(l2);
    if(hint?.l3 && hint.l3.open && L3.includes(hint.l3)) return hint.l3;
    const open=L3.filter(d=>d.open);
    return open.includes(last.l3) ? last.l3 : (open[open.length-1] || null);
  }
  function chooseL4Open(l3, hint){
    if(!l3) return null;
    const L4=kids(l3);
    if(hint?.l4 && hint.l4.open && L4.includes(hint.l4)) return hint.l4;
    const open=L4.filter(d=>d.open);
    return open.includes(last.l4) ? last.l4 : (open[open.length-1] || null);
  }

  function snapshotPath(hint){
    const U=activeUL(); if(!U) return null;
    const T=tops(U);
    const topOpen=chooseTopOpen(U, hint);
    const L2=topOpen? kids(topOpen):[];
    const l2Open=chooseL2Open(topOpen, hint);
    const L3=l2Open? kids(l2Open):[];
    const l3Open=chooseL3Open(l2Open, hint);
    const L4=l3Open? kids(l3Open):[];
    const l4Open=chooseL4Open(l3Open, hint);

    const topH=topOpen && derive(topOpen);
    const l2H =l2Open  && derive(l2Open);
    const l3H =l3Open  && derive(l3Open);
    const l4H =l4Open  && derive(l4Open);
    return {
      top: topOpen ? {h:topH, i:T.indexOf(topOpen), oc:occIndex(T, topOpen, topH)} : {h:null,i:null,oc:null},
      l2 : l2Open  ? {h:l2H,  i:L2.indexOf(l2Open),  oc:occIndex(L2,l2Open,l2H)} : {h:null,i:null,oc:null},
      l3 : l3Open  ? {h:l3H,  i:L3.indexOf(l3Open),  oc:occIndex(L3,l3Open,l3H)} : {h:null,i:null,oc:null},
      l4 : l4Open  ? {h:l4H,  i:L4.indexOf(l4Open),  oc:occIndex(L4,l4Open,l4H)} : {h:null,i:null,oc:null},
    };
  }

  const depth=p=>p ? (p.l4?.h?4 : p.l3?.h?3 : p.l2?.h?2 : p.top?.h?1 : 0) : 0;
  const pathsMatch=(a,b)=>{
    if(!a||!b) return false;
    const eq=(x,y)=>!!x===!!y && (!x || (x.h===y.h && String(x.oc||0)===String(y.oc||0)));
    return eq(a.top,b.top)&&eq(a.l2,b.l2)&&eq(a.l3,b.l3)&&eq(a.l4,b.l4);
  };

  function mergeNonRegressive(prev, next){
    if(!next || !next.top?.h) return prev || null;
    if(!prev || !prev.top?.h) return next;
    if(prev.top.h !== next.top.h) return next;

    const out={ top:next.top, l2:next.l2, l3:next.l3, l4:next.l4 };

    const sameL2 = !!(prev.l2?.h && next.l2?.h && prev.l2.h===next.l2.h);
    const sameL3 = !!(prev.l3?.h && next.l3?.h && prev.l3.h===next.l3.h);

    if(sameL3){
      if(prev.l4?.h && !next.l4?.h) out.l4 = prev.l4;
    } else if(sameL2){
      if(prev.l3?.h && !next.l3?.h){ out.l3 = prev.l3; out.l4 = prev.l4 || {h:null,i:null,oc:null}; }
    } else if(prev.l2?.h && !next.l2?.h){
      out.l2 = prev.l2; out.l3 = prev.l3 || {h:null,i:null,oc:null}; out.l4 = prev.l4 || {h:null,i:null,oc:null};
    }
    return out;
  }

  let restoring=false, restoreMark=0;

  function saveSnapshot(hint){
    const U=activeUL(); if(!U) return;
    const snap=snapshotPath(hint); if(!snap || !snap.top?.h) return;
    if(restoring && (now()-restoreMark)<1000){ log('skip save during restore'); return; }
    const s=read() || {version:VER, ts:now(), menus:{}, last:{menu:null,tab:null}, drawerPath:null};
    s.drawerPath = mergeNonRegressive(s.drawerPath, snap);
    write(s);
    log('save', s.drawerPath);
  }

  const summaryOf=d=>d && d.querySelector(':scope > summary');
  const panelOf  =d=>d && d.querySelector(':scope > .mm-list, :scope > .mm-items, :scope > ul');
  const panelVisible=d=>{ const p=panelOf(d); if(!p) return true; const r=p.getBoundingClientRect(); return r.height>0; };

  function openViaClick(details){
    if(!details) return;
    const s=summaryOf(details);
    if(details.open && panelVisible(details)) return;
    if(!s){ details.open=true; return; }
    if(details.open && !panelVisible(details)) details.open=false;
    s.dispatchEvent(new MouseEvent('click',{bubbles:true,cancelable:true}));
  }
  function openBy(scope, listFn, h, i, oc){
    const list=listFn(scope)||[];
    let target=null;
    if(h) target = pickByHandleOcc(list, h, oc);
    if(!target && typeof i==='number' && i>=0 && i<list.length) target=list[i];
    openViaClick(target);
    return target;
  }

  let watchdogId=null, watchdogUntil=0;
  function kickWatchdog(savedPath){
    const U=activeUL(); if(!U) return;
    watchdogUntil = now()+1200;
    if(watchdogId) return;
    const step=()=>{
      if(now()>watchdogUntil){ watchdogId=null; return; }
      try{
        if(savedPath){
          const top = savedPath.top?.h && pickByHandleOcc(tops(U), savedPath.top.h, savedPath.top.oc);
          const l2  = top && savedPath.l2?.h && pickByHandleOcc(kids(top), savedPath.l2.h, savedPath.l2.oc);
          const l3  = l2  && savedPath.l3?.h && pickByHandleOcc(kids(l2 ), savedPath.l3.h, savedPath.l3.oc);
          const l4  = l3  && savedPath.l4?.h && pickByHandleOcc(kids(l3 ), savedPath.l4.h, savedPath.l4.oc);
          if(top && top.open && !panelVisible(top)) openViaClick(top);
          if(l2  && l2 .open && !panelVisible(l2 )) openViaClick(l2 );
          if(l3  && l3 .open && !panelVisible(l3 )) openViaClick(l3 );
          if(l4  && l4 .open && !panelVisible(l4 )) openViaClick(l4 );
        }
      }catch{}
      watchdogId = requestAnimationFrame(step);
    };
    watchdogId = requestAnimationFrame(step);
  }
  const stopWatchdog=()=>{ if(watchdogId){ cancelAnimationFrame(watchdogId); watchdogId=null; } };

  async function waitDrawerReady(maxMs=1200){
    const start=performance.now();
    return new Promise(res=>{
      const tick=()=>{
        const U=activeUL(), D=activeDrawer();
        if(D && D.open && U) return res();
        if(performance.now()-start>maxMs) return res();
        requestAnimationFrame(tick);
      };
      tick();
    });
  }

  async function maybeRestore(reason='drawer-open'){
    const s=read(); const p=s && s.drawerPath;
    const U=activeUL(); const D=activeDrawer();
    if(!D || !D.open || !U || !p) return;

    const curr=snapshotPath();
    const need = (depth(curr) < depth(p)) || !pathsMatch(curr, p);

    restoring=true; restoreMark=now();
    try{
      if(need){
        await waitDrawerReady();
        const top=openBy(U, tops, p.top.h, p.top.i, p.top.oc);
        const l2 =p.l2?.h ? openBy(top, kids, p.l2.h, p.l2.i, p.l2.oc) : null;
        const l3 =l2 && p.l3?.h ? openBy(l2, kids, p.l3.h, p.l3.i, p.l3.oc) : null;
        if(l3 && p.l4?.h)       openBy(l3, kids, p.l4.h, p.l4.i, p.l4.oc);
      }
      kickWatchdog(p);
    }catch(e){ if (DEBUG) console.warn('[HRB] restore error', e); }
    finally{ setTimeout(()=>{ restoring=false; }, 100); }
  }

  document.addEventListener('toggle', (e) => {
    const d=e.target;
    if(!(d instanceof HTMLDetailsElement)) return;

    if(d.matches('details.menu-drawer-container')){
      if(d.open){ stopWatchdog(); maybeRestore('drawer-open'); }
      else { stopWatchdog(); }
      return;
    }

    const U=activeUL(); if(!U || !U.contains(d)) return;

    if(d.open){
      // one-open-per-level (includes TOP now)
      closeSiblingsAtSameLevel(d);

      const lvl=(d.getAttribute('data-level')||'').toLowerCase();
      if(lvl==='top')      last.top=d;
      else if(lvl==='l2')  last.l2=d;
      else if(lvl==='l3')  last.l3=d;
      else if(lvl==='l4')  last.l4=d;

      saveSnapshot(last);
    }
  }, true);

  document.addEventListener('click', (e) => {
    const U=activeUL(); if(!U) return;
    const el=e.target.closest && e.target.closest('summary, a[href]');
    if(el && U.contains(el)) saveSnapshot(last);
  }, true);

  document.addEventListener('DOMContentLoaded', ()=>{ /* boot */ });
})();

/* =========================
   PART 2 — DESKTOP (v5.2 hardened + make-aware)  (mk7)
   ========================= */
(() => {
  const KEY = 'hrb_menu_state_v5';
  const VERSION = '2025-08-18-mk7';
  const TTL = 30*24*60*60*1000;
  const DEBUG = /\bmenu_debug=1\b/.test(location.search);
  const dlog = (...a)=>{ if (DEBUG) console.log('[HRB desktop]', ...a); };

  const norm = s => (s||'').toString().trim().toLowerCase().replace(/\s+/g,'-').replace(/[^a-z0-9\-_]/g,'');

  const qs = new URLSearchParams(location.search);
  const url = { menu: norm(qs.get('menu')||qs.get('m')||''), tab: norm(qs.get('tab')||qs.get('t')||'') };
  const now = ()=>Date.now?Date.now():new Date().getTime();

  function read(){ try{ const raw=localStorage.getItem(KEY); if(!raw) return null;
    const data=JSON.parse(raw);
    if(!data||data.version!==VERSION||!data.ts||(now()-data.ts)>TTL){ localStorage.removeItem(KEY); return null; }
    return data; }catch{ return null; } }
  function write(state){ try{ localStorage.setItem(KEY, JSON.stringify({ ...state, version:VERSION, ts:now() })); }catch{} }

  function computeMenuHandle(mega){
    const byBase = mega.getAttribute('data-base') || mega.dataset?.base;
    if(byBase) return norm(byBase);
    const byAttr = mega.getAttribute('data-menu-section');
    if(byAttr) return norm(byAttr);
    if(mega.id) return norm(mega.id);
    const sum = mega.querySelector(':scope > summary');
    if(sum && sum.textContent) return norm(sum.textContent);
    const all = Array.from(document.querySelectorAll('.header__inline-menu details.mega-menu, details[id^="Details-HeaderMenu"]'));
    const idx = all.indexOf(mega);
    return idx>=0 ? 'menu-'+idx : 'menu';
  }

  function save(menuHandle, tabId, make){
    if(!menuHandle && !tabId) return;
    const st = read() || { version: VERSION, ts: now(), last:{menu:null,tab:null,make:null}, menus:{} };
    if(menuHandle){ st.last.menu = norm(menuHandle); st.menus[st.last.menu] ||= { tab:null, make:null }; }
    if(typeof tabId==='string' && tabId){
      const key = st.last.menu || menuHandle;
      if(key){
        st.menus[key] ||= { tab:null, make:null };
        st.menus[key].tab = norm(tabId);
        st.last.tab = st.menus[key].tab;
        const mk = make || tabId.split('-')[0] || null;
        if(mk){ st.menus[key].make = norm(mk); st.last.make = st.menus[key].make; }
      }
    } else if (make){
      const key = st.last.menu || menuHandle;
      if(key){ st.menus[key] ||= { tab:null, make:null }; st.menus[key].make = norm(make); st.last.make = st.menus[key].make; }
    }
    write(st);
    dlog('save', st);
  }

  // ---- helpers/selectors
  function findRail(mega){
    return mega.querySelector('.mega-menu--tabs .tabs-rail, .mega-menu__tabs .tabs-rail, .mm-tabs .tabs-rail, [data-tabs-rail]');
  }
  function findLinks(rail){
    return Array.from(rail?.querySelectorAll('a[data-panel], [data-panel-link], a[href^="#"]') || []);
  }
  const idFromLink = a => norm(a?.getAttribute('data-panel') || (a?.getAttribute('href')||'').replace(/^#/, '') || '');
  function tabExistsIn(mega, tabId){
    if(!tabId) return false;
    const rail = findRail(mega);
    return !!findLinks(rail).find(a => idFromLink(a) === norm(tabId));
  }
  function activePanelId(mega){
    const p = mega.querySelector('.tab-panel.is-active, [data-panel-id].is-active');
    return p ? norm(p.id || p.getAttribute('data-panel-id') || '') : null;
  }
  function syncUI(mega){
    // chevrons (aria) reflect actual hidden state
    mega.querySelectorAll('.tabs-make__button[data-make]').forEach(btn=>{
      const list = mega.querySelector(`.tabs-rail__list[data-make="${btn.dataset.make}"]`);
      if(list) btn.setAttribute('aria-expanded', list.hidden ? 'false' : 'true');
    });
    // left-rail active pill for current model
    const id = activePanelId(mega);
    if(id){
      const rail = findRail(mega);
      findLinks(rail).forEach(a=>{
        const on = idFromLink(a) === id;
        a.closest('.tabs-rail__item')?.classList.toggle('is-active', on);
        a.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }
  }

  function showMake(mega, mk){
    mk = norm(mk||''); if(!mk) return false;
    let hit = false;
    mega.querySelectorAll('.tabs-rail__list[data-make]').forEach(ul=>{
      const on = norm(ul.getAttribute('data-make'))===mk;
      ul.hidden = !on ? true : false;
      if(on) hit = true;
    });
    mega.querySelectorAll('.tabs-make__button[data-make]').forEach(btn=>{
      const on = norm(btn.dataset.make)===mk;
      btn.setAttribute('aria-expanded', on?'true':'false');
    });
    return hit;
  }
  function ensureMake(mega, mk, tries=0){
    const MAX = 24; // ~400–500ms
    if(!mk) return;
    if(showMake(mega, mk)){ syncUI(mega); return; }
    if(tries < MAX && mega.open){
      requestAnimationFrame(()=>ensureMake(mega, mk, tries+1));
    }
  }

  function setActiveTab(mega, tabId){
    tabId = norm(tabId||''); if(!tabId) return false;
    if(typeof mega.__activateTab === 'function'){ mega.__activateTab(tabId); return true; }
    const rail = findRail(mega);
    const a = findLinks(rail).find(l => idFromLink(l) === tabId);
    if(a){ a.dispatchEvent(new MouseEvent('click',{bubbles:true,cancelable:true})); return true; }
    return false;
  }
  function ensureTab(mega, wantedId, tries=0){
    const MAX = 24;
    const current = activePanelId(mega);
    if(current === norm(wantedId)){ syncUI(mega); return; }
    setActiveTab(mega, wantedId);
    syncUI(mega);
    if(tries < MAX && mega.open){
      requestAnimationFrame(()=>ensureTab(mega, wantedId, tries+1));
    }
  }

  // pick a saved record that actually matches this mega
  function chooseSavedForMega(mega, menuHint){
    const st = read(); if(!st) return null;
    if(url.tab && tabExistsIn(mega, url.tab)){
      return { menu: norm(url.menu||menuHint||st.last?.menu||''), tab: norm(url.tab), make: norm(url.tab.split('-')[0]||'') };
    }
    if(st.last?.tab && tabExistsIn(mega, st.last.tab)){
      return { menu: st.last.menu, tab: st.last.tab, make: st.last.make || norm(st.last.tab.split('-')[0]||'') };
    }
    const hint = norm(menuHint||''); const rec = hint && st.menus && st.menus[hint];
    if(rec && (tabExistsIn(mega, rec.tab) || rec.make)){
      return { menu: hint, tab: rec.tab || null, make: rec.make || (rec.tab && norm(rec.tab.split('-')[0]||'')) || null };
    }
    return null;
  }

  // save on intent
  function wireDesktopSaving(mega){
    const rail = findRail(mega); if(!rail) return;
    const menuHandle = computeMenuHandle(mega);
    const onChoose = (evt) => {
      const a = evt.target.closest && evt.target.closest('a[data-panel], [data-panel-link], a[href^="#"]');
      if(!a) return;
      const id = idFromLink(a); if(!id) return;
      const mk = a.getAttribute('data-make') || (id.split('-')[0] || null);
      save(menuHandle, id, mk || null);
    };
    findLinks(rail).forEach(a => {
      a.addEventListener('click', onChoose, true);
      a.addEventListener('keydown', e => { if(e.key==='Enter'||e.key===' ') onChoose(e); }, true);
    });
    mega.querySelectorAll('.tab-panel a[href]').forEach(link=>{
      link.addEventListener('click', () => {
        const id = activePanelId(mega); if(!id) return;
        const mkLink = findLinks(rail).find(l => idFromLink(l)===id);
        const mk = mkLink?.getAttribute('data-make') || (id.split('-')[0] || null);
        save(menuHandle, id, mk || null);
      }, true);
    });
    mega.querySelectorAll('.tabs-make__button[data-make]').forEach(btn=>{
      btn.addEventListener('click', ()=> save(menuHandle, null, btn.dataset.make||null));
      btn.addEventListener('keydown', (e)=>{ if(e.key==='Enter'||e.key===' '){ save(menuHandle, null, btn.dataset.make||null); }});
    });
  }

  const appliedOncePerOpen = new WeakSet();
  function applyWhenOpen(mega){
    if(!mega.open || appliedOncePerOpen.has(mega)) return;

    const handle = computeMenuHandle(mega);
    const wanted = chooseSavedForMega(mega, handle);
    if(!wanted || (!wanted.tab && !wanted.make)) return;

    requestAnimationFrame(()=>{
      const mk = wanted.make || (wanted.tab && wanted.tab.split('-')[0]) || null;
      if(mk) ensureMake(mega, mk);          // reliably open the correct make
      if(wanted.tab) ensureTab(mega, wanted.tab); // then stabilize the model/panel
      appliedOncePerOpen.add(mega);
      dlog('restored (mk7)', wanted);
    });
  }

  function onToggle(e){ const mega=e.currentTarget; if(mega.open){ applyWhenOpen(mega); syncUI(mega); } }
  function onHoverOpen(mega){
    const sum = mega.querySelector(':scope > summary'); if(!sum) return;
    const tryApply = () => { applyWhenOpen(mega); syncUI(mega); };
    sum.addEventListener('pointerenter', tryApply, {passive:true});
    sum.addEventListener('focusin', tryApply);
  }

  document.addEventListener('DOMContentLoaded', () => {
    if(url.menu || url.tab){
      const st = read() || {version:VERSION, ts:now(), last:{menu:null,tab:null,make:null}, menus:{}};
      if(url.menu){ st.last.menu = norm(url.menu); st.menus[st.last.menu] ||= {tab:null, make:null}; }
      if(url.tab && st.last.menu){
        st.menus[st.last.menu].tab = norm(url.tab);
        st.menus[st.last.menu].make = norm(url.tab.split('-')[0] || '');
        st.last.tab = st.menus[st.last.menu].tab;
        st.last.make = st.menus[st.last.menu].make;
      }
      write(st);
    }

    const megas = document.querySelectorAll('.header__inline-menu details.mega-menu, details[id^="Details-HeaderMenu"]');
    megas.forEach(mega => {
      mega.addEventListener('toggle', onToggle);
      onHoverOpen(mega);
      wireDesktopSaving(mega);
    });
  });
})();